/** A small copy of the booking site's campaign banner, drawn from the form as it stands. */
import {
  campaignBannerDescription,
  campaignDiscountLabel,
  type CampaignDiscount,
} from '@bonsai/shared';

import { formatDateKey } from '../../../shared/date-time-range/date-keys';
import { formatTimeOfDay } from '../../../shared/date-time-range/time-of-day';

import { formDiscount, isScheduleInOrder, type CampaignFormValues } from './campaign-form';

/**
 * The booking site's own banner colours. They are literal because the preview
 * is a picture of the booking site, which looks the same whatever theme staff
 * have chosen here.
 */
const BANNER = { background: '#2D5A1B', pill: '#97C459', onPill: '#173404', soft: '#EAF3DE' };

function bannerLine(values: CampaignFormValues, discount: CampaignDiscount | null): string {
  if (discount) return campaignBannerDescription({ ...discount, description: values.description });
  return values.description.trim() || '— off every ticket';
}

export function CampaignPreviewBanner({ values }: { values: CampaignFormValues }) {
  const discount = formDiscount(values);
  const { end } = values.schedule;
  return (
    <div
      style={{
        background: BANNER.background,
        color: '#fff',
        borderRadius: 10,
        padding: '12px 13px',
        display: 'flex',
        alignItems: 'center',
        gap: 11,
      }}
    >
      <span
        className="nb-display"
        style={{
          flex: 'none',
          fontSize: 17,
          lineHeight: 1,
          color: BANNER.onPill,
          background: BANNER.pill,
          borderRadius: 7,
          padding: '7px 8px',
          whiteSpace: 'nowrap',
        }}
      >
        {discount ? campaignDiscountLabel(discount) : '— off'}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: BANNER.soft,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {values.name.trim() || 'Campaign name'}
        </div>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            lineHeight: 1.3,
            marginTop: 2,
            overflowWrap: 'anywhere',
          }}
        >
          {bannerLine(values, discount)}
        </div>
        {isScheduleInOrder(values) && (
          <div style={{ fontSize: 11.5, color: BANNER.soft, marginTop: 2 }}>
            Ends {formatDateKey(end.date, { year: false })} at {formatTimeOfDay(end.minutes)}
          </div>
        )}
      </div>
    </div>
  );
}
